import { getCurrentLocaleCode } from "@mongez/localization";
import { requiredRule, useFormControl } from "@mongez/react-form";
import { cn } from "core/shared";
import { isLTR } from "framework/config/translation";
import { CalendarIcon } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import "react-calendar/dist/Calendar.css";
import "react-date-picker/dist/DatePicker.css";
import { twMerge } from "tailwind-merge";
import type { BaseInputProps } from "./BaseInput";
import { ErrorInput } from "./ErrorInput";
import { LabelInput } from "./LabelInput";

const DatePicker = lazy(() =>
  import("react-date-picker").then((module) => ({
    default: module.DatePicker,
  })),
);

type DateInputProps = BaseInputProps & {
  initDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  tileDisabled?: ({ date, view }: { date: Date; view: string }) => boolean;
};
export function DateInput({
  label,
  initDate,
  minDate,
  maxDate,
  classes,
  tileDisabled,
  ...props
}: DateInputProps) {
  const { id, changeValue, error, value } = useFormControl({
    ...props,
    rules: [requiredRule],
  });
  const [selectDate, setSelectedDate] = useState<Date | undefined>(
    value || initDate,
  );
  const lang = getCurrentLocaleCode();

  useEffect(() => {
    if (initDate && !value) {
      changeValue(initDate);
      setSelectedDate(initDate);
    }
  }, [initDate, value, changeValue]);

  const currentDate = new Date();
  const startDate = minDate || currentDate;
  const endDate =
    maxDate ||
    new Date(
      currentDate.getFullYear() + 1,
      currentDate.getMonth(),
      currentDate.getDate(),
      0,
      0,
      0,
    );

  const handleDateChange = (selected) => {
    if (selected) {
      // setOpenDatePicker(false);
      changeValue(selected);
      setSelectedDate(selected);
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", classes?.wrapper)}>
      <LabelInput
        htmlFor={id}
        required={props.required}
        className={cn(error && "text-red-500")}
      >
        {label}
      </LabelInput>

      {/* <button
        type="button"
        onClick={() => setOpenDatePicker(!openDatePicker)}
        className="flex w-full cursor-pointer items-center justify-between gap-4 rounded-full border border-black px-6 py-4 text-xs sm:text-sm lg:text-base"
      >
        <span>{selectDate?.toLocaleDateString() || "Pick a date"}</span>
        <span>
          <Calendar />
        </span>
      </button> */}

      {/* <DatePicker
            selected={selectDate}
            onSelect={handleDateChange}
            classNames={{
              today: "text-primary-default font-bold text-[22px]",
              selected:
                "border-primary-default bg-primary-default text-white rounded-full",
              chevron: "fill-primary-default",
            }}
            modifiers={{
              range_start: startDate,
              range_end: endDate,
            }}
            required
          /> */}
      <Suspense
        fallback={
          <div className="h-12 w-full animate-pulse rounded-full border border-black bg-gray-100" />
        }
      >
        <DatePicker
          value={selectDate}
          onChange={handleDateChange}
          clearIcon={null}
          calendarIcon={<CalendarIcon />}
          className={twMerge(
            "w-full rounded-full border border-black bg-transparent px-6 py-4 outline-none focus-visible:ring-0",
            error ? "border-red-500" : "",
          )}
          format="d/M/yyyy"
          dayPlaceholder={isLTR() ? "DD" : "يوم"}
          monthPlaceholder={isLTR() ? "MM" : "شهر"}
          yearPlaceholder={isLTR() ? "YYYY" : "سنة"}
          calendarProps={{
            minDate: startDate,
            maxDate: endDate,
            defaultValue: initDate || currentDate,
            tileDisabled,
          }}
          locale={lang}
          key={lang}
        />
      </Suspense>
      <ErrorInput>{error}</ErrorInput>
    </div>
  );
}
